import { AnimatePresence, motion } from 'framer-motion';
import type React from 'react';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRequestAuthCode } from '../pages/Verify/verifyCode';

export const RESEND_CODE_DELAY = import.meta.env.VITE_IS_TEST_ENVIRONMENT === 'true' ? 3_000 : 30_000;

export type AuthCodeProps = {
  autoFocus?: boolean;
  disabled?: boolean;
  length?: number;
  onChange: (res: string) => void;
  error?: string;
};

type InputMode = 'text' | 'numeric';

type InputType = 'text' | 'tel' | 'password';

type InputProps = {
  type: InputType;
  inputMode: InputMode;
  pattern: string;
  min?: string;
  max?: string;
};

export type AuthCodeRef = {
  focus: () => void;
  clear: () => void;
};

const inputProps: InputProps = {
  type: 'tel',
  inputMode: 'numeric',
  pattern: '[0-9]{1}',
  min: '0',
  max: '9',
};

export const AuthCode = ({ disabled, length = 6, onChange, error }: AuthCodeProps) => {
  const { mutateAsync: requestAuthCode } = useRequestAuthCode();
  const inputsRef = useRef<Array<HTMLInputElement>>([]);
  const [showResendCode, setShowResendCode] = useState(false);

  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const startShowResendCodeCountdown = useCallback(() => {
    timerRef.current = setTimeout(() => {
      setShowResendCode(true);
    }, RESEND_CODE_DELAY);
  }, []);

  const clickResendCode = useCallback(() => {
    requestAuthCode();
    setShowResendCode(false);
    startShowResendCodeCountdown();
  }, [requestAuthCode, startShowResendCodeCountdown]);

  useEffect(() => {
    startShowResendCodeCountdown();
    return () => clearTimeout(timerRef.current);
  }, [startShowResendCodeCountdown]);

  const inputs = useMemo(() => {
    const sendResult = () => {
      const res = inputsRef.current.map(input => input.value).join('');
      onChange?.(res);
    };
    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const {
        target: { value, nextElementSibling },
      } = e;
      if (value.length > 1) {
        e.target.value = value.charAt(0);
        if (nextElementSibling !== null) {
          (nextElementSibling as HTMLInputElement).focus();
        }
      } else {
        if (value.match(inputProps.pattern)) {
          if (nextElementSibling !== null) {
            (nextElementSibling as HTMLInputElement).focus();
          }
        } else {
          e.target.value = '';
        }
      }
      sendResult();
    };

    const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      const { key } = e;
      const target = e.target as HTMLInputElement;
      if (key === 'Backspace') {
        if (target.value === '') {
          if (target.previousElementSibling !== null) {
            const t = target.previousElementSibling as HTMLInputElement;
            t.value = '';
            t.focus();
            e.preventDefault();
          }
        } else {
          target.value = '';
        }
        sendResult();
      }
    };

    const handleOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      e.target.select();
    };

    const handleOnPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedValue = e.clipboardData.getData('Text');

      let currentInput = 0;

      for (let i = 0; i < pastedValue.length; i++) {
        const pastedCharacter = pastedValue.charAt(i);
        const currentValue = inputsRef.current[currentInput]?.value;
        if (pastedCharacter.match(inputProps.pattern)) {
          if (!currentValue) {
            inputsRef.current[currentInput]!.value = pastedCharacter;
            if (inputsRef.current[currentInput]?.nextElementSibling !== null) {
              (inputsRef.current[currentInput]?.nextElementSibling as HTMLInputElement).focus();
              currentInput++;
            }
          }
        }
      }
      sendResult();

      e.preventDefault();
    };

    const inputsArray: ReactElement[] = [];
    for (let i = 0; i < length; i++) {
      inputsArray.push(
        <input
          autoFocus={i === 0}
          key={i}
          onChange={handleOnChange}
          onKeyDown={handleOnKeyDown}
          onFocus={handleOnFocus}
          onPaste={handleOnPaste}
          {...inputProps}
          ref={el => {
            if (el) {
              inputsRef.current[i] = el;
            }
          }}
          maxLength={1}
          className={`size-12 rounded-md border ${
            error ? 'border-color-border-error' : 'border-color-border'
          } focus:border-color-border-focus text-center text-2xl focus:outline-none`}
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          disabled={disabled}
        />
      );
    }
    return inputsArray;
  }, [disabled, error, length, onChange]);

  return (
    <div className="flex h-16 flex-col gap-1" data-testid="verify-auth-code">
      <div className="flex flex-row items-center justify-center gap-2">{inputs}</div>
      <div className="flex w-full flex-row justify-between text-sm">
        {error && (
          <div className="text-color-text-error" data-testid="verify-auth-code-error">
            {error}
          </div>
        )}
        <AnimatePresence>
          {showResendCode && (
            <motion.button
              className="text-color-bg-primary"
              onClick={clickResendCode}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
              data-testid="verify-resend-code-button"
            >
              Resend Code
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
